import type { NodeImplementation } from '@spotar/runtime';
import type { Object3D } from 'three';
import { applyEaseCurve } from '../helper/easing-helper';
import { runFrameAnimation, stopFrameAnimation } from '../helper/frame-animation-helper';
import { sceneHelper } from '../helper/scene-helper';
import { readObjectRefId } from '../helper/object-ref';
import {
  applyObjectVisibility,
  collectFadeMaterials,
  setMaterialsOpacity,
} from '../helper/visibility-helper';

type FadeVisibleData = {
  target?: unknown;
  visible: boolean;
  duration?: number;
  curve?: string;
};

function getFadeAnimationKey(objectUuid: string): string {
  return `opacity:${objectUuid}`;
}

function waitForDuration(durationSeconds: number): Promise<void> {
  if (durationSeconds <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    setTimeout(resolve, durationSeconds * 1000);
  });
}

function animateMaterialsOpacity(
  objectUuid: string,
  materials: ReturnType<typeof collectFadeMaterials>,
  startOpacities: number[],
  endOpacity: number,
  durationSeconds: number,
  curve: string | undefined
): Promise<boolean> {
  return runFrameAnimation({
    key: getFadeAnimationKey(objectUuid),
    durationSeconds,
    onProgress: (progress) => {
      const easedProgress = applyEaseCurve(progress, curve);
      for (let index = 0; index < materials.length; index++) {
        const startOpacity = startOpacities[index] ?? endOpacity;
        materials[index].transparent = true;
        materials[index].opacity = startOpacity + (endOpacity - startOpacity) * easedProgress;
      }
    },
    onComplete: () => setMaterialsOpacity(materials, endOpacity),
  });
}

async function runFadeOnObject(
  object: Object3D,
  isVisible: boolean,
  durationSeconds: number,
  curve: string | undefined
): Promise<void> {
  stopFrameAnimation(getFadeAnimationKey(object.uuid));

  const materials = collectFadeMaterials(object);
  const wasVisible = object.visible;

  if (materials.length === 0) {
    if (isVisible) {
      applyObjectVisibility(object, true);
      await waitForDuration(durationSeconds);
      return;
    }
    await waitForDuration(durationSeconds);
    applyObjectVisibility(object, false);
    return;
  }

  if (isVisible) {
    const startOpacities = materials.map((material) => (wasVisible ? material.opacity : 0));
    if (!wasVisible) {
      setMaterialsOpacity(materials, 0);
    }
    applyObjectVisibility(object, true);
    await animateMaterialsOpacity(object.uuid, materials, startOpacities, 1, durationSeconds, curve);
    return;
  }

  const startOpacities = materials.map((material) => material.opacity);
  const hasFadedOut = await animateMaterialsOpacity(
    object.uuid,
    materials,
    startOpacities,
    0,
    durationSeconds,
    curve
  );
  if (hasFadedOut) applyObjectVisibility(object, false);
}

export const runFadeVisibleImplementation: NodeImplementation = async (data, _nodeId) => {
  const { target, visible, duration, curve } = data as FadeVisibleData;
  const targetId = readObjectRefId(target);
  if (!targetId) throw new Error('no target object is set on this node');

  const object = sceneHelper?.getObjectByUUID(targetId);
  if (!object) {
    console.error(`Object ${targetId} not found`);
    return;
  }

  const durationSeconds = duration ?? 0.5;
  await runFadeOnObject(object, visible, durationSeconds, curve);
};
